import React, { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import AddOptionModal from "../AddOptionModal";
import CSVImportModal from "components/Pricer/CSVImportModal";
import QuickAdd from "components/QuickAdd/QuickAdd";
import { prefetchShorthandGrammar } from "components/QuickAdd/useShorthandGrammar";
import TradeCaptureModal from "components/TradeCapture/TradeCaptureModal";
import VolSurfaceComparison from "components/Common/FilterButtons/VolSurfaceComparison";
import { PRICER_TOOLS } from "./usePricerTools";

/**
 * Hosts the modals opened from the Pricer toolbar, plus the Quick Add
 * grammar prefetch and the global "/" Quick Add shortcut.
 *
 * @param tools          result of usePricerTools()
 * @param onQuickAddHotkey called with "hotkey" when "/" is pressed outside an input
 */
const PricerToolModals = ({
  tools,
  onQuickAddHotkey,
  refetchOptionRow,
  dark_Theme,
}) => {
  const { isOpen, quickAddFocusKey, setters } = tools;
  const queryClient = useQueryClient();

  // Warm the shorthand grammar cache so Quick Add opens ready to parse.
  useEffect(() => {
    prefetchShorthandGrammar(queryClient);
  }, [queryClient]);

  // Global "/" opens Quick Add, or focuses the bar if already open.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target;
      const tag = target?.tagName?.toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        target?.isContentEditable
      ) {
        return;
      }
      e.preventDefault();
      onQuickAddHotkey("hotkey");
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onQuickAddHotkey]);

  return (
    <>
      <CSVImportModal
        isOpen={isOpen[PRICER_TOOLS.CSV_IMPORT]}
        onClose={setters[PRICER_TOOLS.CSV_IMPORT]}
        refetchOptionRow={refetchOptionRow}
      />

      <QuickAdd
        isOpen={isOpen[PRICER_TOOLS.QUICK_ADD]}
        onClose={setters[PRICER_TOOLS.QUICK_ADD]}
        refetchOptionRow={refetchOptionRow}
        focusKey={quickAddFocusKey}
      />

      <TradeCaptureModal
        open={isOpen[PRICER_TOOLS.TRADE_CAPTURE]}
        onClose={() => setters[PRICER_TOOLS.TRADE_CAPTURE](false)}
        refetchOptionRow={refetchOptionRow}
      />

      <VolSurfaceComparison
        variant="none"
        dark_Theme={dark_Theme}
        open={isOpen[PRICER_TOOLS.VOL_COMPARISON]}
        onClose={() => setters[PRICER_TOOLS.VOL_COMPARISON](false)}
      />

      {/*Show Add Option Modal*/}
      {isOpen[PRICER_TOOLS.ADD_OPTION] && (
        <AddOptionModal
          setIsAddOptionModalVisible={setters[PRICER_TOOLS.ADD_OPTION]}
          isAddOptionModalVisible={isOpen[PRICER_TOOLS.ADD_OPTION]}
        />
      )}
    </>
  );
};

export default PricerToolModals;
